import {
  type EventContentObjectDetail,
  type Program,
  generateProgramEventPublishAt,
  generateProgramEventStartDate,
  getProgramEventEndDate,
} from 'models';
import { useMemo } from 'react';
import { generateAttachmentsInputInitialValue, generateUploadFile, getDatePickerDateType } from 'ui';

import type { DefaultEventFormValues } from '../DefaultEventForm';

type UseDefaultEventFormInitialValuesProperties = {
  eventContentObject?: EventContentObjectDetail;
  initialData?: Partial<DefaultEventFormValues>;
  isDuplicating: boolean;
  isProgram: boolean;
  program?: Program;
};

export const useDefaultEventFormInitialValues = ({
  eventContentObject,
  initialData,
  isDuplicating,
  isProgram,
  program,
}: UseDefaultEventFormInitialValuesProperties): Partial<DefaultEventFormValues> =>
  // eslint-disable-next-line complexity -- TODO: fix
  useMemo(() => {
    const event = eventContentObject?.event;
    const publishAt = generateProgramEventPublishAt({
      isDuplicating,
      isProgram,
      publishAt: eventContentObject?.publishAt,
    });
    const startTime = generateProgramEventStartDate({ isDuplicating, publishAt, startTime: event?.startTime });
    const duration = event?.duration ?? 30;
    const endTime = getProgramEventEndDate({ duration, endTime: event?.endTime, isDuplicating, startTime });

    const programContentObject = eventContentObject?.contentableType === 'Program' ? eventContentObject : undefined;

    const initialProgramToUse = programContentObject?.program ?? program;

    return {
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
      coverPhoto: event?.coverPhoto ? generateUploadFile({ response: event?.coverPhoto }) : undefined,
      duplicateInviteesFromEvent: isDuplicating ? eventContentObject?.event.id : undefined,
      duration,
      emailInvitees: [],
      endTime: getDatePickerDateType(endTime),
      eventsFiles: generateAttachmentsInputInitialValue(
        // eslint-disable-next-line @typescript-eslint/naming-convention -- TODO: Remove this once we have a proper event form
        event?.eventsFiles.map((file) => ({ ...file, content_type: file.contentType })),
      ),
      featuredInChannels: event?.featuredInChannels ?? false,
      htmlContent: event?.htmlContent ?? '',
      id: event?.id ?? '',
      invitees: [],
      name: event?.name ?? '',
      programId: initialProgramToUse?.id,
      programTopicId: initialData?.programTopicId ?? programContentObject?.programsTopic.id,
      publishAt: getDatePickerDateType(publishAt),
      showInChannels: event?.showInChannels ?? false,
      startTime: getDatePickerDateType(startTime),
      tags: event?.tags?.map((tagItem) => tagItem.id) ?? [],
      textContent: event?.textContent ?? '',
      viewPermission: program ? 'public' : (event?.viewPermission ?? 'private'),
    };
  }, [eventContentObject, initialData, isDuplicating, isProgram, program]);
